import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Activity, AlertTriangle, Lock, Loader2, Upload } from 'lucide-react';
import { Card } from './index';
import { VERZUIM_QUERY_KEY, getVerzuimOverzicht, VerzuimMaand } from '../services/payrollBridgeApi';

/**
 * Menu Data: FTE-gewogen ziekteverzuim (CBS-norm) als trendlijn over 12
 * maanden, gevoed door de maandsnapshots van de Payroll Bridge. Bij een
 * verlopen personeelslijst (≥ 90 dagen) ligt een DATA-LOCK-overlay over de
 * grafiek; verversen gebeurt in Mijn Cockpit tab 7.
 */

const MAAND_KORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const UPLOAD_LINK = '/employer/mijn-belagom?tab=flexbudget';
const LIJN = '#12284A';

const pctFmt = (v: number | null) => (v === null ? '—' : `${v.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`);

const Trendlijn: React.FC<{ trend: VerzuimMaand[] }> = ({ trend }) => {
  const { t } = useTranslation('employer');
  const [hover, setHover] = useState<number | null>(null);
  // Tekenen op de echte pixelbreedte: labels blijven 11px op desktop én mobiel
  const wrapRef = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const meet = () => setW(Math.max(280, Math.round(el.clientWidth)));
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 220;
  const padL = 44;
  const padR = 16;
  const padT = 16;
  const padB = 28;
  const waarden = trend.map((m) => m.verzuim_pct);
  const max = Math.max(1, ...waarden.filter((v): v is number => v !== null));
  const yMax = Math.ceil(max * 1.2 * 2) / 2; // ruimte boven de top, halve procenten
  const x = (i: number) => padL + (i * (W - padL - padR)) / Math.max(1, trend.length - 1);
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / yMax);
  const punten = trend.map((m, i) => (m.verzuim_pct === null ? null : { x: x(i), y: y(m.verzuim_pct), i }));
  // Lijnsegmenten alleen tussen opeenvolgende maanden met een waarde
  const paden: string[] = [];
  let huidig: string[] = [];
  punten.forEach((p) => {
    if (p) huidig.push(`${p.x},${p.y}`);
    else if (huidig.length) { paden.push(huidig.join(' ')); huidig = []; }
  });
  if (huidig.length) paden.push(huidig.join(' '));
  const ticks = [0, yMax / 2, yMax];
  const h = hover !== null ? trend[hover] : null;

  return (
    <div className="relative" ref={wrapRef}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block max-w-full" role="img" aria-label={t('verzuim.grafiekAria', 'Ziekteverzuimpercentage per maand, FTE-gewogen')}>
        {ticks.map((tv) => (
          <g key={tv}>
            <line x1={padL} x2={W - padR} y1={y(tv)} y2={y(tv)} stroke="#e5e7eb" strokeWidth={1} />
            <text x={padL - 6} y={y(tv) + 4} textAnchor="end" fontSize={11} fill="#6b7280">{tv.toLocaleString('nl-NL')}%</text>
          </g>
        ))}
        {trend.map((m, i) => (W < 480 && (trend.length - 1 - i) % 2 === 1 ? null :
          <text key={`${m.jaar}-${m.maand}`} x={x(i)} y={H - 8} textAnchor="middle" fontSize={11} fill="#6b7280">
            {MAAND_KORT[m.maand - 1]}{m.maand === 1 || i === 0 ? ` '${String(m.jaar).slice(2)}` : ''}
          </text>
        ))}
        {paden.map((d, i) => (
          <polyline key={i} points={d} fill="none" stroke={LIJN} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {punten.map((p) => p && (
          <g key={p.i}>
            <circle cx={p.x} cy={p.y} r={hover === p.i ? 6 : 4} fill={LIJN} stroke="#fff" strokeWidth={2} />
            {/* Groter hit-target dan de marker */}
            <rect x={p.x - 14} y={padT} width={28} height={H - padT - padB} fill="transparent" onMouseEnter={() => setHover(p.i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(p.i)} onBlur={() => setHover(null)} tabIndex={0} />
          </g>
        ))}
        {hover !== null && punten[hover] && (
          <line x1={punten[hover]!.x} x2={punten[hover]!.x} y1={padT} y2={H - padB} stroke="#9ca3af" strokeDasharray="3 3" />
        )}
      </svg>
      {h && h.verzuim_pct !== null && (
        <div className="absolute top-2 right-2 rounded-md border border-gray-200 bg-white shadow-sm px-3 py-2 text-xs text-gray-700 pointer-events-none">
          <p className="font-semibold text-gray-900">{MAAND_KORT[h.maand - 1]} {h.jaar}: {pctFmt(h.verzuim_pct)}</p>
          <p>{t('verzuim.tooltip', { defaultValue: '{{z}} ziekmelding(en) · {{d}} FTE-ziektedagen · {{fte}} FTE actief', z: h.aantal_ziekmeldingen ?? 0, d: (h.ziekte_fte_dagen ?? 0).toLocaleString('nl-NL'), fte: (h.fte_som ?? 0).toLocaleString('nl-NL') })}</p>
        </div>
      )}
    </div>
  );
};

export const VerzuimTrend: React.FC = () => {
  const { t } = useTranslation('employer');
  const [toonTabel, setToonTabel] = useState(false);
  const { data, isLoading, isError } = useQuery({ queryKey: VERZUIM_QUERY_KEY, queryFn: getVerzuimOverzicht });

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#12284A]" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('verzuim.titel', 'Ziekteverzuim (CBS-norm, FTE-gewogen)')}</h2>
            <p className="text-xs text-gray-500">{t('verzuim.sub', 'Σ ziektedagen × FTE ÷ (Σ FTE actief × kalenderdagen) · maandsnapshots uit de Payroll Bridge')}</p>
          </div>
        </div>
        {data && data.huidig_pct !== null && (
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{pctFmt(data.huidig_pct)}</p>
            <p className="text-xs text-gray-500">{t('verzuim.laatsteMaand', 'laatste afgesloten maand')}</p>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-500 py-8"><Loader2 className="w-4 h-4 animate-spin" />{t('verzuim.laden', 'Verzuimcijfers laden…')}</div>
      ) : isError || !data ? (
        <p className="text-sm text-red-700">{t('verzuim.fout', 'Verzuimcijfers konden niet worden geladen.')}</p>
      ) : (
        <div className="relative">
          {data.freshness.niveau === 'waarschuwing' && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {t('verzuim.waarschuwing', { defaultValue: 'Uw personeelsdata is {{dagen}} dagen oud. Ververs de export vóór dag {{lock}} om de grafieken ontgrendeld te houden.', dagen: data.freshness.dagen_oud, lock: data.freshness.lock_vanaf_dagen })}{' '}
                <Link to={UPLOAD_LINK} className="underline font-medium">{t('verzuim.verversLink', 'Nu verversen')}</Link>
              </span>
            </div>
          )}

          <div className={data.data_lock ? 'blur-[2px] opacity-60 pointer-events-none select-none' : ''} aria-hidden={data.data_lock}>
            <Trendlijn trend={data.trend} />
          </div>

          {data.data_lock && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="max-w-md rounded-xl border border-red-200 bg-white/95 shadow-lg p-5 text-center">
                <Lock className="w-8 h-8 text-red-600 mx-auto" />
                {data.bridge_actief ? (
                  <>
                    <p className="mt-2 font-semibold text-red-800">🚨 {t('verzuim.lockTitel', 'DATA-LOCK: Personeelslijst is verlopen.')}</p>
                    <p className="mt-1 text-sm text-gray-700">{t('verzuim.lockTekst', 'Nieuwe maandsnapshots kunnen pas berekend worden zodra de actuele FTE-omvang per afdeling is ververst. Upload uw nieuwste export binnen 60 seconden om de grafieken te ontgrendelen.')}</p>
                  </>
                ) : (
                  <>
                    <p className="mt-2 font-semibold text-gray-900">{t('verzuim.geenBridgeTitel', 'Payroll Bridge nog niet ingericht')}</p>
                    <p className="mt-1 text-sm text-gray-700">{t('verzuim.geenBridgeTekst', 'Upload uw HR-export in Mijn Cockpit (L&O-flexbudget) om het FTE-gewogen verzuimpercentage per maand en per afdeling te laten berekenen.')}</p>
                  </>
                )}
                <Link to={UPLOAD_LINK} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#12284A] px-4 py-2 text-sm font-medium text-white hover:bg-[#015a90]">
                  <Upload className="w-4 h-4" />
                  {t('verzuim.uploadKnop', '📁 Upload nu actuele HR-export')}
                </Link>
              </div>
            </div>
          )}

          {!data.data_lock && (
            <>
              {data.per_afdeling.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-900 mb-2">{t('verzuim.perAfdeling', 'Per afdeling (laatste maand)')}</p>
                  <div className="flex flex-wrap gap-2">
                    {data.per_afdeling.map((a) => (
                      <span key={a.department_id} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-800">
                        {a.naam}: <span className="font-semibold">{a.onder_privacy_grendel ? t('verzuim.grendel', { defaultValue: '< {{n}} pers.', n: data.min_groep }) : pctFmt(a.laatste_pct)}</span>
                        {a.onder_privacy_grendel && <Lock className="w-3 h-3 text-gray-400" aria-label={t('verzuim.grendelAria', 'privacy-grendel')} />}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <button type="button" onClick={() => setToonTabel((v) => !v)} className="mt-3 text-xs text-[#12284A] underline">
                {toonTabel ? t('verzuim.tabelVerberg', 'Tabel verbergen') : t('verzuim.tabelToon', 'Toon als tabel')}
              </button>
              {toonTabel && (
                <table className="mt-2 w-full text-xs text-left">
                  <thead className="text-gray-500">
                    <tr><th className="py-1">{t('verzuim.kolMaand', 'Maand')}</th><th>{t('verzuim.kolPct', 'Verzuim %')}</th><th>{t('verzuim.kolZiek', 'Ziekmeldingen')}</th><th>{t('verzuim.kolFte', 'FTE actief')}</th></tr>
                  </thead>
                  <tbody>
                    {data.trend.map((m) => (
                      <tr key={`${m.jaar}-${m.maand}`} className="border-t border-gray-100">
                        <td className="py-1">{MAAND_KORT[m.maand - 1]} {m.jaar}</td>
                        <td>{pctFmt(m.verzuim_pct)}</td>
                        <td>{m.aantal_ziekmeldingen ?? '—'}</td>
                        <td>{m.fte_som?.toLocaleString('nl-NL') ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
};

export default VerzuimTrend;
